import { useRef, useState } from "react";
import { Crown, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { submitRegistration } from "@/lib/api";
import { CATEGORIES } from "@/lib/site";

const initial = {
  full_name: "", gender: "", age: "", city: "", mobile: "", email: "",
  category: "", instagram: "", experience: "", talent: "", preferred_event: "", message: "", website: "",
};

export default function Register({ events }) {
  const [form, setForm] = useState(initial);
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    if (f.size > 10 * 1024 * 1024) { toast.error("Photograph must be under 10MB."); return; }
    setPhoto(f);
    setPreview(URL.createObjectURL(f));
  };

  const validate = () => {
    const er = {};
    if (!form.full_name.trim()) er.full_name = "Full name is required";
    if (!form.gender) er.gender = "Please select";
    if (!form.age || isNaN(form.age) || +form.age < 4 || +form.age > 100) er.age = "Enter a valid age";
    if (!form.city.trim()) er.city = "City is required";
    if (!/^[6-9]\d{9}$/.test(form.mobile.trim())) er.mobile = "Enter a valid 10-digit mobile number";
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) er.email = "Enter a valid email";
    if (!form.category) er.category = "Please choose a category";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) { toast.error("Please fix the highlighted fields."); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (photo) fd.append("photo", photo);
      const res = await submitRegistration(fd);
      setSuccess(res.message);
      setForm(initial);
      setPhoto(null);
      setPreview(null);
      toast.success("Registration received!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const Field = ({ k, label, type = "text", placeholder = "", testId }) => (
    <div>
      <label className="block text-xs tracking-[0.2em] uppercase text-muted mb-2">{label}{["full_name","gender","age","city","mobile","email","category"].includes(k) && <span className="text-fuchsia"> *</span>}</label>
      <input type={type} value={form[k]} onChange={set(k)} placeholder={placeholder} className={`input-lux ${errors[k] ? "border-fuchsia/70" : ""}`} data-testid={testId || `reg-${k}-input`} />
      {errors[k] && <p className="mt-1.5 text-xs text-fuchsia" data-testid={`reg-${k}-error`}>{errors[k]}</p>}
    </div>
  );

  return (
    <section id="register" className="py-28 lg:py-36 bg-royal-900/40 border-y border-gold/10 spotlight-top" data-testid="register-section">
      <div className="max-w-4xl mx-auto px-5 lg:px-8">
        <SectionHeading
          eyebrow="Registration"
          title={<>Begin Your <span className="gold-text">Journey</span></>}
          sub="One form between you and the spotlight. Fill it with pride."
          testId="register-heading"
        />

        {success ? (
          <Reveal>
            <div className="glass-card rounded-md p-12 text-center gold-frame" data-testid="registration-success">
              <Crown className="mx-auto text-gold animate-float" size={44} />
              <h3 className="mt-6 font-display text-3xl gold-text">Welcome to the House</h3>
              <p className="mt-4 text-cream/85 max-w-md mx-auto leading-relaxed">{success}</p>
              <button
                onClick={() => setSuccess("")}
                className="mt-8 rounded-full border border-gold/50 px-8 py-3 text-xs font-semibold tracking-widest text-gold hover:bg-gold/10 transition-colors"
                data-testid="register-another-btn"
              >
                REGISTER ANOTHER TALENT
              </button>
            </div>
          </Reveal>
        ) : (
          <Reveal>
            <form onSubmit={submit} className="glass-card rounded-md p-7 sm:p-10 grid sm:grid-cols-2 gap-6" data-testid="registration-form">
              <Field k="full_name" label="Full Name" placeholder="Your full name" />
              <div>
                <label className="block text-xs tracking-[0.2em] uppercase text-muted mb-2">Gender <span className="text-fuchsia">*</span></label>
                <select value={form.gender} onChange={set("gender")} className={`input-lux ${errors.gender ? "border-fuchsia/70" : ""}`} data-testid="reg-gender-select">
                  <option value="" className="bg-royal-900">Select</option>
                  {["Female", "Male", "Other"].map((g) => <option key={g} value={g} className="bg-royal-900">{g}</option>)}
                </select>
                {errors.gender && <p className="mt-1.5 text-xs text-fuchsia" data-testid="reg-gender-error">{errors.gender}</p>}
              </div>
              <Field k="age" label="Age" type="number" placeholder="Your age" />
              <Field k="city" label="City" placeholder="Your city" />
              <Field k="mobile" label="Mobile Number" type="tel" placeholder="10-digit mobile" />
              <Field k="email" label="Email" type="email" placeholder="you@example.com" />
              <div>
                <label className="block text-xs tracking-[0.2em] uppercase text-muted mb-2">Category <span className="text-fuchsia">*</span></label>
                <select value={form.category} onChange={set("category")} className={`input-lux ${errors.category ? "border-fuchsia/70" : ""}`} data-testid="reg-category-select">
                  <option value="" className="bg-royal-900">Choose your stage</option>
                  {CATEGORIES.map((c) => <option key={c.name} value={c.name} className="bg-royal-900">{c.name}</option>)}
                </select>
                {errors.category && <p className="mt-1.5 text-xs text-fuchsia" data-testid="reg-category-error">{errors.category}</p>}
              </div>
              <Field k="instagram" label="Instagram ID" placeholder="@yourhandle" />
              <Field k="experience" label="Experience" placeholder="e.g. Fresher / 2 years" />
              <Field k="talent" label="Talent / Profession" placeholder="e.g. Model, Dancer" />
              <div className="sm:col-span-2">
                <label className="block text-xs tracking-[0.2em] uppercase text-muted mb-2">Preferred Event</label>
                <select value={form.preferred_event} onChange={set("preferred_event")} className="input-lux" data-testid="reg-event-select">
                  <option value="" className="bg-royal-900">Select an event</option>
                  {events.map((ev) => <option key={ev.id} value={ev.name} className="bg-royal-900">{ev.name} — {ev.city}, {ev.date}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs tracking-[0.2em] uppercase text-muted mb-2">Message</label>
                <textarea rows={3} value={form.message} onChange={set("message")} placeholder="Tell us about yourself…" className="input-lux resize-none" data-testid="reg-message-input" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs tracking-[0.2em] uppercase text-muted mb-2">Upload Photograph</label>
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="w-full border border-dashed border-gold/40 rounded-md p-6 flex items-center justify-center gap-4 text-muted hover:border-gold hover:text-gold transition-colors duration-300"
                  data-testid="reg-photo-upload"
                >
                  {preview ? (
                    <>
                      <img src={preview} alt="Photograph preview" className="h-16 w-16 object-cover rounded-full ring-1 ring-gold/50" />
                      <span className="text-sm text-cream/80">{photo?.name}</span>
                    </>
                  ) : (
                    <>
                      <Upload size={20} />
                      <span className="text-sm">Click to upload your photograph (image, max 10MB)</span>
                    </>
                  )}
                </button>
                <input ref={fileRef} type="file" accept="image/*" onChange={onPhoto} className="hidden" data-testid="reg-photo-input" />
              </div>

              <input type="text" name="website" value={form.website} onChange={set("website")} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />

              <div className="sm:col-span-2 text-center pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="rounded-full bg-gold text-royal-950 px-12 py-4 text-sm font-semibold tracking-widest transition-all duration-300 hover:bg-gold-light hover:shadow-[0_0_35px_rgba(212,175,55,0.55)] disabled:opacity-60 inline-flex items-center gap-3"
                  data-testid="reg-submit-btn"
                >
                  {loading && <Loader2 size={18} className="animate-spin" />}
                  SUBMIT REGISTRATION
                </button>
              </div>
            </form>
          </Reveal>
        )}
      </div>
    </section>
  );
}
